/**
 * The small secondary detail beside a trace step, shared by web and native.
 *
 * A step's primary line is the verb ("Searching jobs…"); this is the object of
 * that verb when the arguments actually carry one — the search query, the CV
 * title being tailored, the person a message is going to. It is only ever
 * derived from data already on the wire, never invented, and it is short enough
 * to sit on the same line without pushing the label off it.
 */
import { splitToolName } from '@/lib/tool-name';

export type StepMeta = {
  text: string;
  /** Rendered in the monospace face — identifiers, queries, filenames. */
  mono?: boolean;
  /** A search-style step gets the magnifier treatment on the row. */
  kind?: 'query' | 'target';
};

const MAX_META_CHARS = 40;

const QUERY_KEYS = ['query', 'keywords', 'q', 'search', 'searchQuery', 'jobTitle', 'role', 'title'];
const TARGET_KEYS = ['title', 'name', 'jobTitle', 'companyName', 'to', 'recipient', 'skillId', 'skillName'];

const SEARCH_ACTIONS = new Set(['search', 'recommend', 'recall', 'find']);

function firstString(record: Record<string, unknown>, keys: readonly string[]): string | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim().length > 0) return value.trim();
  }
  return undefined;
}

function clip(text: string): string {
  if (text.length <= MAX_META_CHARS) return text;
  return `${text.slice(0, MAX_META_CHARS - 1).trimEnd()}…`;
}

export function resolveStepMeta(params: {
  toolName: string;
  args?: unknown;
}): StepMeta | undefined {
  const { toolName, args } = params;
  if (!args || typeof args !== 'object' || Array.isArray(args)) return undefined;
  const record = args as Record<string, unknown>;
  const [, action] = splitToolName(toolName);
  const actionRoot = action.replace(/[A-Z].*$/, '');

  if (SEARCH_ACTIONS.has(actionRoot) || toolName.toLowerCase().includes('search')) {
    const query = firstString(record, QUERY_KEYS);
    return query ? { text: clip(query), kind: 'query' } : undefined;
  }

  const target = firstString(record, TARGET_KEYS);
  return target ? { text: clip(target), kind: 'target' } : undefined;
}
